import { useState, type FormEvent } from 'react';
import { Modal } from '../../components/Modal';
import { getErrorMessage } from '../../lib/errors';
import { PLATFORM_OPTIONS } from '../../lib/platforms';
import type { ContentGoalsRead, ContentGoalsUpdate, MetricTarget } from '../../types/api';
import { monthLabel } from './calendarDates';

/** A week of daily posting is the ceiling the stepper offers. */
const MAX_WEEKLY = 7;

/** Enough rows to describe a month; more is a spreadsheet, not a goal. */
const MAX_METRICS = 10;

/** Editing shape: inputs hold strings, so a half-typed number is not a zero. */
interface MetricRow {
  key: string;
  label: string;
  target: string;
  current: string;
}

function toRows(targets: readonly MetricTarget[]): MetricRow[] {
  return targets.map((target, index) => ({
    key: `saved-${index}`,
    label: target.label,
    target: String(target.target),
    current: target.current === null ? '' : String(target.current),
  }));
}

function toCadence(goals: ContentGoalsRead | null): Record<string, number> {
  const cadence: Record<string, number> = {};
  for (const option of PLATFORM_OPTIONS) {
    const value = goals?.cadence_targets[option.value] ?? 0;
    cadence[option.value] = Number.isFinite(value) ? Math.min(Math.max(value, 0), MAX_WEEKLY) : 0;
  }
  return cadence;
}

interface GoalsModalProps {
  month: string;
  goals: ContentGoalsRead | null;
  onSave: (body: ContentGoalsUpdate) => Promise<unknown>;
  onDismiss: () => void;
}

/**
 * What this month is for: one line of focus, a posting rhythm per platform, and
 * the numbers the user is watching.
 *
 * Everything is optional and nothing is enforced — a cadence target moves a
 * meter on the strip, it never blocks a post or spends a generation. A platform
 * left at zero simply drops off the strip.
 */
export function GoalsModal({ month, goals, onSave, onDismiss }: GoalsModalProps) {
  const [focus, setFocus] = useState(goals?.focus ?? '');
  const [cadence, setCadence] = useState<Record<string, number>>(() => toCadence(goals));
  const [metrics, setMetrics] = useState<MetricRow[]>(() => toRows(goals?.metric_targets ?? []));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function setWeekly(platform: string, value: number) {
    const clamped = Math.min(Math.max(value, 0), MAX_WEEKLY);
    setCadence((current) => ({ ...current, [platform]: clamped }));
  }

  function patchMetric(key: string, patch: Partial<MetricRow>) {
    setMetrics((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSaving(true);
    setError(null);

    // A zero is "not tracking this platform", so it is dropped rather than sent.
    const cadenceTargets: Record<string, number> = {};
    for (const [platform, weekly] of Object.entries(cadence)) {
      if (weekly > 0) cadenceTargets[platform] = weekly;
    }

    const metricTargets: MetricTarget[] = metrics
      .filter((row) => row.label.trim().length > 0)
      .map((row) => ({
        label: row.label.trim(),
        target: Number(row.target) || 0,
        current: row.current.trim() === '' ? null : Number(row.current) || 0,
      }));

    try {
      await onSave({
        focus: focus.trim() || null,
        cadence_targets: cadenceTargets,
        metric_targets: metricTargets,
      });
      onDismiss();
    } catch (caught) {
      setError(getErrorMessage(caught, 'Those goals did not save.'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal title={`Goals for ${monthLabel(month)}`} kind="content goals" onDismiss={onDismiss}>
      <form className="form" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <label className="field">
          <span className="field__label">Focus</span>
          <textarea
            className="field__input"
            rows={3}
            maxLength={500}
            placeholder="What this month is for — one line you can hold a post against."
            value={focus}
            onChange={(event) => setFocus(event.target.value)}
          />
        </label>

        <fieldset className="kb-fieldset">
          <legend className="field__label">Posts a week</legend>
          <p className="field__hint">Zero means the platform stays off the strip.</p>

          <ul className="cadence-editor">
            {PLATFORM_OPTIONS.map((option) => {
              const weekly = cadence[option.value] ?? 0;
              return (
                <li className="cadence-editor__row" key={option.value}>
                  <span className="cadence-editor__platform">{option.label}</span>
                  <div className="stepper">
                    <button
                      className="stepper__step"
                      type="button"
                      aria-label={`One fewer ${option.label} post a week`}
                      disabled={weekly <= 0}
                      onClick={() => setWeekly(option.value, weekly - 1)}
                    >
                      –
                    </button>
                    <input
                      className="stepper__value"
                      type="number"
                      min={0}
                      max={MAX_WEEKLY}
                      inputMode="numeric"
                      aria-label={`${option.label} posts a week`}
                      value={weekly}
                      onChange={(event) => setWeekly(option.value, Number(event.target.value) || 0)}
                    />
                    <button
                      className="stepper__step"
                      type="button"
                      aria-label={`One more ${option.label} post a week`}
                      disabled={weekly >= MAX_WEEKLY}
                      onClick={() => setWeekly(option.value, weekly + 1)}
                    >
                      +
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </fieldset>

        <fieldset className="kb-fieldset">
          <legend className="field__label">Numbers you are watching</legend>

          {metrics.length > 0 ? (
            <ul className="metric-editor">
              {metrics.map((row) => (
                <li className="metric-editor__row" key={row.key}>
                  <label className="field">
                    <span className="visually-hidden">Metric</span>
                    <input
                      className="field__input"
                      type="text"
                      maxLength={80}
                      placeholder="New followers"
                      value={row.label}
                      onChange={(event) => patchMetric(row.key, { label: event.target.value })}
                    />
                  </label>
                  <label className="field field--number">
                    <span className="visually-hidden">Target</span>
                    <input
                      className="field__input"
                      type="number"
                      min={0}
                      inputMode="numeric"
                      placeholder="Target"
                      value={row.target}
                      onChange={(event) => patchMetric(row.key, { target: event.target.value })}
                    />
                  </label>
                  <label className="field field--number">
                    <span className="visually-hidden">Current</span>
                    <input
                      className="field__input"
                      type="number"
                      min={0}
                      inputMode="numeric"
                      placeholder="Now"
                      value={row.current}
                      onChange={(event) => patchMetric(row.key, { current: event.target.value })}
                    />
                  </label>
                  <button
                    className="button button--ghost button--danger-text"
                    type="button"
                    aria-label={`Remove ${row.label || 'metric'}`}
                    onClick={() =>
                      setMetrics((current) => current.filter((entry) => entry.key !== row.key))
                    }
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {metrics.length < MAX_METRICS ? (
            <button
              className="button"
              type="button"
              onClick={() =>
                setMetrics((current) => [
                  ...current,
                  {
                    key: `row-${Date.now()}-${current.length}`,
                    label: '',
                    target: '',
                    current: '',
                  },
                ])
              }
            >
              Add a number
            </button>
          ) : (
            <p className="field__hint">Ten is the limit. Drop one to add another.</p>
          )}
        </fieldset>

        {error ? (
          <p className="form__error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="modal__actions">
          <button className="button" type="button" onClick={onDismiss} disabled={isSaving}>
            Cancel
          </button>
          <button className="button button--primary" type="submit" disabled={isSaving}>
            {isSaving ? 'Saving' : 'Save goals'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
